/**
 * meterRequest.js —— 智能抄表系统管理 API 独立请求实例（V3.6 接口文档）
 *
 * 与项目里已有的 request.js 完全隔离：
 *   独立的 axios 实例、独立的拦截器、独立的 token 存储、独立的环境配置，
 *   互不干扰，可直接并行使用。
 *
 * 能力：
 *   1. 所有请求自动以 application/x-www-form-urlencoded 表单格式提交
 *      （axios 传 URLSearchParams 时会自动带该 Content-Type，这里再显式兜底）；
 *   2. 自动向每个请求体注入公共参数 token + admin_user_name，调用方无需手写；
 *   3. token 失效自动处理：
 *      - 主动：本地记录 token 获取时间，距过期不足 buffer 时，发请求前先静默续期；
 *      - 被动：服务端返回业务码 1006（Token 不存在）/ 1007（Token 已过期）时，
 *        用配置的账号密码静默重新登录，拿到新 token 后自动重发"原请求"；
 *   4. 并发保护：多个请求同时失效时只触发一次登录（共享同一个登录 Promise）；
 *      若失效响应到达时 token 已被其他请求刷新，直接用新 token 重发，不再多登录一次；
 *   5. 防死循环：每个请求最多因 token 失效重试 1 次（MAX_RETRY）；
 *   6. 登录请求使用原生 axios 直发，绕过本实例拦截器，避免循环依赖。
 *
 * 对接说明（文档 V3.6）：
 *   - 登录：POST /user/login          参数 admin_user_name / api_password，返回 data 即 token
 *   - token 有效期 2 小时
 *   - 业务码：200 成功；1006 Token 不存在；1007 Token 已过期；1011 访问过快（每秒限 2 次）
 *   - 所有业务接口 body 均需携带 token + admin_user_name
 *
 * 使用前提：反向代理已将 baseURL 指向 http://imrapi.jrhsz.com
 *   （vite / vue.config.js 代理配置示例见文件底部注释）
 */

import axios from 'axios'

/* ============================== 配 置 区 ============================== */

export const METER_API_CONFIG = {
  /**
   * 反向代理前缀：按你实际的代理规则修改。
   * 例如代理规则为 '/meter-api' -> 'http://imrapi.jrhsz.com'，则这里填 '/meter-api'，
   * 实际请求为 /meter-api/user/login、/meter-api/meter/meterList …
   */
  baseURL: '/meterApi',

  /** 抄表平台 API 账号密码（也可在运行时调用 setCredentials() 注入） */
  username: '日钢机械',
  password: 'rjq123456',

  /** 请求超时（毫秒） */
  timeout: 30 * 1000,

  /** token 本地缓存的 localStorage key */
  tokenStorageKey: 'METER_API_TOKEN',

  /** token 有效期：文档标注 2 小时 */
  tokenTTL: 2 * 60 * 60 * 1000,

  /** 提前量：距过期不足 5 分钟时，主动静默续期，避免请求打到服务端才失败 */
  tokenBuffer: 5 * 60 * 1000,

  /** 视为"token 失效"的业务码（1009 账号与 token 不符也可按需加入） */
  invalidTokenCodes: [1006, 1007],

  /** 单个请求因 token 失效最多重发的次数（重发后仍失效则报错，防死循环） */
  maxRetry: 1
}

/* ============================ token 存取 ============================ */

let tokenCache = null // { token, expireAt } 内存缓存

function loadToken() {
  if (tokenCache) return tokenCache
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(METER_API_CONFIG.tokenStorageKey)
    if (raw) tokenCache = JSON.parse(raw)
  } catch (e) {
    /* localStorage 不可用（如 SSR）时仅用内存缓存 */
  }
  return tokenCache
}

function saveToken(token) {
  tokenCache = { token, expireAt: Date.now() + METER_API_CONFIG.tokenTTL }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(METER_API_CONFIG.tokenStorageKey, JSON.stringify(tokenCache))
    }
  } catch (e) {
    /* 忽略持久化失败 */
  }
  return tokenCache
}

export function getToken() {
  const t = loadToken()
  return t ? t.token : null
}

/** 判断本地 token 是否可用（存在且未临近过期） */
function isTokenUsable() {
  const t = loadToken()
  return !!t && !!t.token && Date.now() < t.expireAt - METER_API_CONFIG.tokenBuffer
}

/** 清空本地 token（登出、切换账号、强制下次重新登录时使用） */
export function clearToken() {
  tokenCache = null
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(METER_API_CONFIG.tokenStorageKey)
    }
  } catch (e) {
    /* 忽略 */
  }
}

/** 运行时注入 / 修改账号密码（修改后建议 clearToken() 重新登录） */
export function setCredentials(username, password) {
  METER_API_CONFIG.username = username
  METER_API_CONFIG.password = password
}

/* ======================= 静默登录（并发共享） ======================= */

let refreshingPromise = null

/**
 * 静默登录（含并发去重）：
 * 任意时刻只会有一个真实登录请求在途，其余并发调用共享同一个 Promise。
 */
export function silentLogin() {
  if (!refreshingPromise) {
    refreshingPromise = doLogin().finally(() => {
      refreshingPromise = null
    })
  }
  return refreshingPromise
}

/** 真实登录动作：用原生 axios 直发，绕过本实例拦截器，避免死循环 */
async function doLogin() {
  const { baseURL, timeout, username, password } = METER_API_CONFIG
  const body = new URLSearchParams()
  body.append('admin_user_name', username)
  body.append('api_password', password)

  const res = await axios.post('/user/login', body, {
    baseURL,
    timeout,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  })

  const data = res && res.data
  if (data && data.code === 200 && typeof data.data === 'string' && data.data) {
    saveToken(data.data)
    return data.data
  }
  // 1002 账号不存在或没有 API 权限；1005 密码错误 等
  const code = data && data.code
  const msg = (data && data.msg) || '登录失败'
  const err = new Error(`[meterRequest] 静默登录失败：${msg}（code=${code}）`)
  err.code = code
  throw err
}

/** 发请求前保证 token 可用：不可用（不存在 / 临过期）则先静默续期 */
function ensureToken() {
  return isTokenUsable() ? Promise.resolve(getToken()) : silentLogin()
}

/* ========================= axios 实 例 ========================= */

const meterRequest = axios.create({
  baseURL: METER_API_CONFIG.baseURL,
  timeout: METER_API_CONFIG.timeout
})

/* ---------- 请求拦截器：表单序列化 + 公共参数注入 ---------- */

meterRequest.interceptors.request.use(
  async (config) => {
    // 1) 保存原始参数对象：重发时用它重新序列化、重新注入最新 token
    //    （首次请求 data 是普通对象；重发时我们会把它恢复成普通对象）
    if (config.data && !(config.data instanceof URLSearchParams) && !config._rawData) {
      config._rawData = { ...config.data }
    }

    // 2) 保证 token 可用：无 token / 临过期 → 先静默登录（并发共享一次）
    const token = await ensureToken()

    // 3) 注入文档要求的公共参数：token、admin_user_name（调用方传了也会被覆盖）
    const raw = { ...(config._rawData || {}) }
    raw.token = token
    raw.admin_user_name = METER_API_CONFIG.username

    // 4) 序列化为 x-www-form-urlencoded 表单
    const form = new URLSearchParams()
    Object.keys(raw).forEach((k) => {
      const v = raw[k]
      form.append(k, v === undefined || v === null ? '' : String(v))
    })
    config.data = form

    // 兼容 axios 0.x（plain object headers）与 1.x（AxiosHeaders）
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Content-Type', 'application/x-www-form-urlencoded')
    } else {
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }

    // 5) 记录本次实际使用的 token：响应失效时用于判断"是否已被其他请求刷新"
    config._tokenUsed = token
    return config
  },
  (error) => Promise.reject(error)
)

/* ---------- 响应拦截器：失效续期 + 自动重发原请求 ---------- */

meterRequest.interceptors.response.use(
  async (response) => {
    const body = response.data
    const cfg = response.config

    // 业务码命中"token 失效"（1006 / 1007）
    if (body && METER_API_CONFIG.invalidTokenCodes.indexOf(body.code) !== -1) {
      // 超过重试上限：直接失败，避免死循环
      cfg._retryCount = (cfg._retryCount || 0) + 1
      if (cfg._retryCount > METER_API_CONFIG.maxRetry) {
        return Promise.reject(
          new Error(`[meterRequest] token 续期后请求仍失败：${body.msg || ''}（code=${body.code}）`)
        )
      }

      const current = getToken()
      if (current && current !== cfg._tokenUsed) {
        // token 已被其他并发请求刷新过 → 直接用新 token 重发，无需再登录
      } else {
        // 本地 token 与服务端一致地失效 → 静默重新登录
        try {
          await silentLogin()
        } catch (loginErr) {
          return Promise.reject(loginErr)
        }
      }

      // 恢复原始参数对象 → 重新走请求拦截器（重新序列化 + 注入最新 token）
      cfg.data = cfg._rawData
      return meterRequest(cfg)
    }

    // 正常情况：直接返回响应体（{ code, data, msg, totalCount }）
    return body
  },
  (error) => {
    // HTTP 层错误（反向代理 502/504、网络断开、超时等）
    const msg = error && error.response
      ? `HTTP ${error.response.status}`
      : (error && error.message) || '网络异常'
    return Promise.reject(new Error(`[meterRequest] 请求失败：${msg}`))
  }
)
export default meterRequest